/**
 * Advanced Data Grid Demo
 * Showcases TGDataTable with client and server pagination
 */

import React, { useState, useEffect } from 'react';
import { Card, Alert, Space, Tag, Button } from 'antd';
import { TGDataTable } from './common';
import { Section } from './layout';
import { colors, spacing } from '../theme/designTokens';

// Generate mock data
const generateMockData = (count = 200) => {
  const statuses = ['pass', 'fail', 'warning', 'pending'];
  const severities = ['critical', 'high', 'medium', 'low'];
  const categories = ['Security', 'Performance', 'Reliability', 'Compliance'];

  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    name: `Finding ${i + 1}`,
    description: `This is a detailed description for finding ${i + 1}`,
    status: statuses[i % 4],
    severity: severities[i % 4],
    category: categories[i % 4],
    score: Math.floor(Math.random() * 100),
    count: Math.floor(Math.random() * 50) + 1,
    timestamp: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
  }));
};

/**
 * Client Mode Demo
 */
function ClientModeDemo() {
  const data = generateMockData(200);

  const columns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      width: 150,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      width: 300,
      filterable: true,
      filterType: 'text',
      render: (text) => (
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Pass', value: 'pass' },
        { label: 'Fail', value: 'fail' },
        { label: 'Warning', value: 'warning' },
        { label: 'Pending', value: 'pending' },
      ],
      render: (status) => {
        const colorMap = {
          pass: 'green',
          fail: 'red',
          warning: 'orange',
          pending: 'blue',
        };
        return <Tag color={colorMap[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      key: 'severity',
      title: 'Severity',
      dataIndex: 'severity',
      width: 120,
      sortable: true,
      filterable: true,
      filterType: 'multiselect',
      filterOptions: [
        { label: 'Critical', value: 'critical' },
        { label: 'High', value: 'high' },
        { label: 'Medium', value: 'medium' },
        { label: 'Low', value: 'low' },
      ],
      render: (severity) => {
        const colorMap = {
          critical: 'red',
          high: 'orange',
          medium: 'gold',
          low: 'green',
        };
        return <Tag color={colorMap[severity]}>{severity.toUpperCase()}</Tag>;
      },
    },
    {
      key: 'category',
      title: 'Category',
      dataIndex: 'category',
      width: 150,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Security', value: 'Security' },
        { label: 'Performance', value: 'Performance' },
        { label: 'Reliability', value: 'Reliability' },
        { label: 'Compliance', value: 'Compliance' },
      ],
    },
    {
      key: 'score',
      title: 'Score',
      dataIndex: 'score',
      width: 100,
      sortable: true,
      filterable: true,
      filterType: 'range',
      render: (score) => (
        <span style={{ fontWeight: 600, color: score > 70 ? colors.status.pass : colors.status.fail }}>
          {score}
        </span>
      ),
    },
    {
      key: 'count',
      title: 'Count',
      dataIndex: 'count',
      width: 100,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
  ];

  return (
    <Section title="Client Mode - 200 Rows (filters, sorts, pages locally)">
      <Alert
        type="info"
        message="Client-side data handling"
        description="All 200 rows loaded. Filtering, sorting, and pagination happen in the browser. Settings persist to localStorage using presetKey='demo.client'."
        showIcon
        style={{ marginBottom: spacing.md }}
      />

      <TGDataTable
        columns={columns}
        data={data}
        rowKey="id"
        pagination="client"
        pageSize={10}
        presetKey="demo.client"
        showExport
        showColumnConfig
        showFilters
        exportFilename="client-mode-export"
      />
    </Section>
  );
}

/**
 * Server Mode Demo
 */
function ServerModeDemo() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(null);

  // Mock server data (simulate API)
  const fullDataset = React.useMemo(() => generateMockData(500), []);

  // Simulate server-side fetch
  useEffect(() => {
    if (!query) return;

    setLoading(true);

    // Simulate network delay
    const timeout = setTimeout(() => {
      let result = [...fullDataset];

      // Apply server-side filters
      Object.entries(query.filters).forEach(([key, value]) => {
        if (!value) return;

        result = result.filter((row) => {
          switch (key) {
            case 'name':
              return row.name.toLowerCase().includes(value.toLowerCase());
            case 'status':
              return row.status === value;
            case 'severity':
              return Array.isArray(value) ? value.includes(row.severity) : true;
            case 'category':
              return row.category === value;
            case 'score':
              if (value.min !== undefined && row.score < value.min) return false;
              if (value.max !== undefined && row.score > value.max) return false;
              return true;
            default:
              return true;
          }
        });
      });

      // Apply server-side sort
      if (query.sort) {
        result.sort((a, b) => {
          const aVal = a[query.sort.key];
          const bVal = b[query.sort.key];
          if (aVal === bVal) return 0;
          const comparison = aVal < bVal ? -1 : 1;
          return query.sort.order === 'asc' ? comparison : -comparison;
        });
      }

      // Apply server-side pagination
      const start = (query.page - 1) * query.pageSize;
      const end = start + query.pageSize;
      const page = result.slice(start, end);

      setData(page);
      setTotal(result.length);
      setLoading(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [query, fullDataset]);

  const columns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      sortable: true,
    },
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Pass', value: 'pass' },
        { label: 'Fail', value: 'fail' },
        { label: 'Warning', value: 'warning' },
        { label: 'Pending', value: 'pending' },
      ],
      render: (status) => {
        const colorMap = {
          pass: 'green',
          fail: 'red',
          warning: 'orange',
          pending: 'blue',
        };
        return <Tag color={colorMap[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      key: 'severity',
      title: 'Severity',
      dataIndex: 'severity',
      width: 120,
      sortable: true,
      filterable: true,
      filterType: 'multiselect',
      filterOptions: [
        { label: 'Critical', value: 'critical' },
        { label: 'High', value: 'high' },
        { label: 'Medium', value: 'medium' },
        { label: 'Low', value: 'low' },
      ],
      render: (severity) => {
        const colorMap = {
          critical: 'red',
          high: 'orange',
          medium: 'gold',
          low: 'green',
        };
        return <Tag color={colorMap[severity]}>{severity.toUpperCase()}</Tag>;
      },
    },
    {
      key: 'category',
      title: 'Category',
      dataIndex: 'category',
      width: 150,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Security', value: 'Security' },
        { label: 'Performance', value: 'Performance' },
        { label: 'Reliability', value: 'Reliability' },
        { label: 'Compliance', value: 'Compliance' },
      ],
    },
    {
      key: 'score',
      title: 'Score',
      dataIndex: 'score',
      width: 100,
      sortable: true,
      filterable: true,
      filterType: 'range',
      render: (score) => (
        <span style={{ fontWeight: 600, color: score > 70 ? colors.status.pass : colors.status.fail }}>
          {score}
        </span>
      ),
    },
  ];

  // Mock fetchAll for export
  const fetchAll = async () => {
    // In real app, this would call an API endpoint to get all data
    return fullDataset;
  };

  return (
    <Section title="Server Mode - 500 Rows (filters, sorts, pages on server)">
      <Alert
        type="success"
        message="Server-side data handling"
        description="Table emits query changes to onQueryChange. Parent component fetches data from server with filters, sort, and pagination applied. Settings persist to localStorage using presetKey='demo.server'."
        showIcon
        style={{ marginBottom: spacing.md }}
      />

      <TGDataTable
        columns={columns}
        data={data}
        rowKey="id"
        total={total}
        loading={loading}
        pagination="server"
        pageSize={20}
        onQueryChange={setQuery}
        presetKey="demo.server"
        showExport
        showColumnConfig
        showFilters
        fetchAll={fetchAll}
        exportFilename="server-mode-export"
        onRefresh={() => setQuery({ ...query })}
      />
    </Section>
  );
}

/**
 * Main Data Grid Tab
 */
function DataGridTab() {
  return (
    <div>
      <Alert
        type="info"
        message="Advanced Data Grid System"
        description="TGDataTable is a reusable, feature-rich grid with per-column filters, column visibility, localStorage persistence, CSV/XLSX export, and both client and server pagination modes. All settings persist across page reloads."
        showIcon
        closable
        style={{ marginBottom: spacing.lg }}
      />

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <ClientModeDemo />
        <ServerModeDemo />
      </Space>

      <Section title="Features" style={{ marginTop: spacing.lg }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Card size="small">
            <strong>✓ Per-Column Filters</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Text search, number, select, multi-select, date, date range, and numeric range filters.
              Text inputs debounced 300ms.
            </p>
          </Card>

          <Card size="small">
            <strong>✓ Column Visibility</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Show/hide columns with checkbox popover. Select all/deselect all. Reset to defaults.
            </p>
          </Card>

          <Card size="small">
            <strong>✓ Persistent Presets</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Column visibility, page size, filters, and sort persist to localStorage using presetKey.
            </p>
          </Card>

          <Card size="small">
            <strong>✓ Export CSV & XLSX</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Export current page or full dataset (if fetchAll provided). Include/exclude hidden columns.
              Custom exportTransform per column.
            </p>
          </Card>

          <Card size="small">
            <strong>✓ Client & Server Pagination</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Client mode: all data loaded, filtered/sorted/paged in browser. Server mode: emits query
              changes, parent fetches paginated data.
            </p>
          </Card>

          <Card size="small">
            <strong>✓ Keyboard Accessible</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Tab into filter controls. Space toggles column checkboxes. Enter applies filter.
            </p>
          </Card>

          <Card size="small">
            <strong>✓ Consistent Theming</strong>
            <p style={{ margin: 0, color: 'rgba(23, 5, 43, 0.65)' }}>
              Uses design tokens for colors, spacing, typography. Matches Forms & Filters pattern.
            </p>
          </Card>
        </Space>
      </Section>
    </div>
  );
}

export default DataGridTab;
